import { useState } from 'react';
import { Input, Switch } from '@/components/ui';
import { patchClientConnections, type InterviewStyleDetail } from '@/data/client';
import type { ClientFormProps } from './types';
import { CONNECTION_TABS, connectionsFromClient, pickConnections } from './data';
import { useDraft } from './useDraft';
import { SaveFooter } from './SaveFooter';
import { PromptModal } from './PromptModal';

const KEYS = CONNECTION_TABS.content;

/* contentIQ and Expert Studio: how interviews are conducted, the nudges, and
   whether the client can add jobs and galleries themselves. */
export function ContentForm({ client, styles, onSaved, onDirtyChange }: ClientFormProps & {
  styles: InterviewStyleDetail[];
}) {
  const f = useDraft({
    formId: 'content',
    initial: connectionsFromClient(client),
    keys: KEYS,
    persist: (d) => patchClientConnections(client.orgId, pickConnections(d, KEYS)),
    onSaved, onDirtyChange,
  });
  const { draft: conn, set: setC } = f;
  const [promptStyle, setPromptStyle] = useState<string | null>(null);

  return (
    <div className="admin-form">
      <div className="admin-form__body">
        <section className="admin-sect">
          <h4 className="admin-sect__title">AI interviews &amp; images</h4>
          <div className="admin-prof">
            <Switch
              label="AI-guided interviews"
              checked={conn.aiInterviewEnabled}
              onChange={(e) => setC('aiInterviewEnabled', e.target.checked)}
            />
            {/* contentIQ hero images only. Expert Studio photos and the
                Authenticity Vault stay real client material either way. */}
            <Switch
              label="AI hero images on blogs & articles"
              checked={conn.aiImagesEnabled}
              onChange={(e) => setC('aiImagesEnabled', e.target.checked)}
            />
            {conn.aiInterviewEnabled && (
              <label className="tr-field">
                <span className="tr-field__label">Interview style</span>
                <div className="tr-field__control">
                  <select
                    className="tr-select"
                    value={conn.interviewStyle}
                    onChange={(e) => setC('interviewStyle', e.target.value)}
                  >
                    {styles.length === 0 && <option value={conn.interviewStyle}>{conn.interviewStyle}</option>}
                    {styles.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                  </select>
                </div>
                <button type="button" className="admin-linkbtn" onClick={() => setPromptStyle(conn.interviewStyle)}>
                  View prompt
                </button>
              </label>
            )}
          </div>
        </section>

        <section className="admin-sect">
          <h4 className="admin-sect__title">Expert Studio</h4>
          <div className="admin-prof">
            <Switch
              label="Expert Studio (client self-service)"
              checked={conn.selfServiceEnabled}
              onChange={(e) => setC('selfServiceEnabled', e.target.checked)}
            />
            {conn.selfServiceEnabled && (
              <>
                <p className="admin-hint" style={{ gridColumn: '1 / -1', margin: 0 }}>
                  Lets this client add jobs and build image galleries that publish to their Astro site. Only enable for Astro sites with the site repo configured (Setup &rarr; Push to site).
                </p>
                <Switch
                  label="Save page FAQs to the site"
                  checked={conn.faqPushEnabled}
                  onChange={(e) => setC('faqPushEnabled', e.target.checked)}
                />
                <p className="admin-hint" style={{ gridColumn: '1 / -1', margin: 0 }}>
                  {conn.faqPushEnabled
                    ? 'FAQ pages can be written to the repo as JSON. Only turn this on once this client\u2019s Astro site has the FAQ component \u2014 otherwise files are written and nothing reads them.'
                    : 'Off: FAQs still collect in the Vault, ground their content and reach the team \u2014 nothing is written to the site. Turn on once their Astro site renders the FAQ JSON.'}
                </p>
                <Switch
                  label="Let the chatbot answer from these FAQs"
                  checked={conn.faqChatEnabled}
                  onChange={(e) => setC('faqChatEnabled', e.target.checked)}
                />
                <p className="admin-hint" style={{ gridColumn: '1 / -1', margin: 0 }}>
                  {conn.faqChatEnabled
                    ? 'conversationIQ answers visitors from this client\u2019s APPROVED FAQs as well as its own knowledge list. Check \u201cWhat it knows\u201d under conversationIQ to see exactly what that adds.'
                    : 'Off: their FAQs ground the content we write, but the website chatbot never quotes them. Turn on after reading \u201cWhat it knows\u201d \u2014 that is a live chatbot on their site.'}
                </p>
                <Switch
                  label="Hold client FAQ edits for review"
                  checked={conn.faqReviewHold}
                  onChange={(e) => setC('faqReviewHold', e.target.checked)}
                />
                <p className="admin-hint" style={{ gridColumn: '1 / -1', margin: 0 }}>
                  {conn.faqReviewHold
                    ? 'On: what the client writes reaches the Vault immediately but waits for approval before it can appear on their page.'
                    : 'Off: this client\u2019s FAQs are approved as they write them. Only for clients whose copy you trust unreviewed.'}
                </p>
              </>
            )}
          </div>
        </section>

        <section className="admin-sect">
          <h4 className="admin-sect__title">Reminders</h4>
          <div className="admin-prof">
            <Switch
              label="Content interview reminders"
              checked={conn.contentReminderEnabled}
              onChange={(e) => setC('contentReminderEnabled', e.target.checked)}
            />
            {conn.contentReminderEnabled && (
              <>
                <p className="admin-hint" style={{ gridColumn: '1 / -1', margin: 0 }}>
                  Emails all client users to schedule a project interview after inactivity; repeats until they submit a new request.
                </p>
                <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 12 }}>
                  <Input label="First reminder after (days)" inputMode="numeric" value={conn.contentReminderDays} onChange={(e) => setC('contentReminderDays', e.target.value)} />
                  <Input label="Repeat every (days)" inputMode="numeric" value={conn.contentReminderInterval} onChange={(e) => setC('contentReminderInterval', e.target.value)} />
                </div>
              </>
            )}
          </div>
        </section>
      </div>
      <SaveFooter dirty={f.dirty} saving={f.saving} err={f.err} onSave={f.save} label="Save content settings" />

      {promptStyle && (
        <PromptModal
          style={styles.find((s) => s.id === promptStyle) ?? null}
          styleId={promptStyle}
          onClose={() => setPromptStyle(null)}
        />
      )}
    </div>
  );
}
